import { create } from 'zustand'

import {
  getGetStardustBalanceApi,
  getGetStarsBalanceApi,
  getGetTapsBalanceApi,
} from '@api'
import { webApp } from '@shared'

import { createSelectors, Mapper } from './helpers'

export interface UserBank {
  id: number
  title: string
  description: string
  isSolved: boolean
  isAvailable: boolean
  image?: string
}

export interface StarDustBalance {
  balance: number
}

export interface TapBalanceBackend {
  balance: number
  limit: number
  last_increment: string
  taplaps_time: string
}

export interface TapBalance {
  balance: number
  limit: number
  lastIncrement: string
  taplapsTime: string
}

const TapsMapper: Mapper<TapBalanceBackend, TapBalance> = {
  mapFromBackend: (data) => ({
    balance: data.balance,
    limit: data.limit,
    lastIncrement: data.last_increment,
    taplapsTime: data.taplaps_time,
  }),
  mapToBackend: (data) => ({
    balance: data.balance,
    limit: data.limit,
    last_increment: data.lastIncrement,
    taplaps_time: data.taplapsTime,
  }),
}

function getNextRefillTime(refillDate: string, taplapsTime: string): Date {
  const lastRefill = new Date(refillDate)
  const boostEnd = new Date(taplapsTime)

  let nextRefill = new Date(lastRefill.getTime() + 30 * 60 * 1000)

  if (lastRefill >= boostEnd) {
    nextRefill = new Date(lastRefill.getTime() + 60 * 60 * 1000)
  }

  if (lastRefill < boostEnd && nextRefill > boostEnd) {
    nextRefill = new Date(lastRefill.getTime() + 60 * 60 * 1000)
  }

  return nextRefill
}

interface UserBankState {
  isLoading: boolean
  isError: boolean
  stardust: StarDustBalance | null
  stars: number | null
  taps: TapBalance | null
  tapsErrorAt: number | null
  refillTimerId: NodeJS.Timeout | null
  actions: {
    getGetTapsBalance: (signal?: AbortSignal) => void
    getGetStardustBalance: () => void
    getGetStarsBalance: (signal?: AbortSignal) => Promise<void>
    incrementTaps: () => void
    decrementTaps: () => number | null
    incrementStars: () => number | null
    decrementStars: () => void
    clearRefillTimer: () => void
    startRefillTimer: () => void
  }
}

export const useUserBankStoreBase = create<UserBankState>((set, get) => ({
  isLoading: true,
  isError: false,
  stardust: null,
  stars: null,
  taps: null,
  tapsErrorAt: null,
  refillTimerId: null,

  actions: {
    getGetTapsBalance: async (signal?: AbortSignal) => {
      set({ isLoading: true })

      const { data, ok } = await getGetTapsBalanceApi(signal)

      if (signal?.aborted) return

      if (ok && data) {
        const taps = TapsMapper.mapFromBackend(data)
        set({ taps })

        // перезапускаем таймер
        get().actions.clearRefillTimer()
        get().actions.startRefillTimer()
      }

      set({ isLoading: false })
    },

    incrementTaps: () => {
      set((state) => {
        if (!state.taps) return state
        if (state.taps.balance === state.taps.limit) {
          webApp.HapticFeedback.notificationOccurred('error')
          return { tapsErrorAt: Date.now() }
        }
        return { taps: { ...state.taps, balance: state.taps.balance + 1 } }
      })
    },

    decrementTaps: () => {
      let newBalance: number | null = null

      set((state) => {
        if (!state.taps) return state

        if (state.taps.balance === 0) {
          webApp.HapticFeedback.notificationOccurred('error')
          return { tapsErrorAt: Date.now() }
        }

        newBalance = state.taps.balance - 1
        return { taps: { ...state.taps, balance: newBalance } }
      })

      if (newBalance !== null) {
        const taps = get().taps
        if (taps && newBalance < taps.limit) {
          get().actions.startRefillTimer()
        }
      }

      return newBalance
    },

    decrementStars: () => {
      set((state) => {
        if (state.stars == null) return state
        return { stars: state.stars - 1 }
      })
    },

    incrementStars: () => {
      let newStars: number | null = null

      set((state) => {
        if (state.stars == null) return state
        newStars = state.stars + 1
        return { stars: newStars }
      })

      return newStars
    },

    getGetStardustBalance: async () => {
      set({ isLoading: true })

      const { data, ok } = await getGetStardustBalanceApi()
      if (ok && data) {
        set({ stardust: data })
      }

      set({ isLoading: false })
    },

    getGetStarsBalance: async (signal?: AbortSignal) => {
      set({ isLoading: true })

      const { data, ok } = await getGetStarsBalanceApi(signal)

      if (signal?.aborted) return

      if (ok) {
        set({ stars: data })
      }

      set({ isLoading: false })
    },

    clearRefillTimer: () => {
      const { refillTimerId } = get()
      if (refillTimerId) {
        clearTimeout(refillTimerId)
        set({ refillTimerId: null })
      }
    },

    startRefillTimer: () => {
      const { taps, refillTimerId } = get()
      if (!taps) return
      if (taps.balance >= taps.limit) return
      if (refillTimerId) return

      const nextTapTime = getNextRefillTime(
        taps.lastIncrement,
        taps.taplapsTime
      )
      const msUntilNext = nextTapTime.getTime() - Date.now()

      if (msUntilNext > 0) {
        const id = setTimeout(() => {
          get().actions.getGetTapsBalance()
        }, msUntilNext + 1000)
        set({ refillTimerId: id })
      }
    },
  },
}))

export const useUserBankActions = () =>
  useUserBankStoreBase((state) => state.actions)
export const useUserBankTaps = () => useUserBankStoreBase((state) => state.taps)
export const useUserBankTapsErrorAt = () =>
  useUserBankStoreBase((state) => state.tapsErrorAt)
export const useUserBankStars = () =>
  useUserBankStoreBase((state) => state.stars)

export const useUserBank = createSelectors(useUserBankStoreBase)
